import { ChangeDetectionStrategy, Component, input, signal } from '@angular/core';
import { PluginsModule } from '@uigy/modules/plugins/plugins.module';
import { ActionButtonDefinition } from '@uigy/shared/components/ui-buttons-action-bar/models';
import { SubSectionType } from '@uigy/shared/components/ui-drawer/components/ui-drawer-section/constants';
import { DrawerSubSection } from '@uigy/shared/components/ui-drawer/components/ui-drawer-section/interface';
import { UiGridComponent } from '@uigy/shared/components/ui-grid/components/ui-grid/ui-grid.component';
import { UiLongTextComponent } from '@uigy/shared/components/ui-long-text/ui-long-text.component';
import { UiAccordionComponent } from '@uigy/shared/components/ui-accordion/ui-accordion.component';
import { UiSectionTitleComponent } from '@uigy/shared/components/ui-section-title/ui-section-title.component';
import {
  UiGridKeyValueComponent,
} from '@uigy/shared/components/ui-grid/components/ui-grid-key-value/ui-grid-key-value.component';
import { UiSectionContentComponent } from '@uigy/shared/components/ui-section-content/ui-section-content.component';

@Component({
  selector: 'app-ui-drawer-section',

  imports: [
    UiSectionTitleComponent,
    UiLongTextComponent,
    PluginsModule,
    UiGridComponent,
    UiGridKeyValueComponent,
    UiAccordionComponent,
    UiSectionContentComponent,
  ],
  templateUrl: './ui-drawer-section.component.html',
  styleUrl: './ui-drawer-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiDrawerSectionComponent {
  public title = input<string>();
  public subtitle = input<string>();
  public actionButtons = input<ActionButtonDefinition[]>();
  public subSections = input.required<DrawerSubSection[]>();

  // This dictionary keeps track of which grid subsection has data in it
  // it is done in a separate signal to avoid over writing data when multiple async calls are made
  private readonly rowsDict = signal<Record<number, boolean>>({});
  private readonly accordionExpandedDict = signal<Record<string, boolean>>({});

  protected readonly SubSectionType = SubSectionType;

  public onShouldShowGrid(index: number, value: boolean): void {
    this.rowsDict.set({ ...this.rowsDict(), [index]: !value });
  }

  public shouldShowTitle(index: number): boolean {
    const subSection = this.subSections()[index];
    if (!subSection.title) {
      return false;
    }

    switch (subSection.subSectionProps.subSectionType) {
      case SubSectionType.longText:
        return true;
      case SubSectionType.keyValueGrid:
        return this.rowsDict()[index] ?? false;
      default:
        return true;
    }
  }

  private getAccordionKey(subSectionIndex: number, accordionIndex: number): string {
    return `${subSectionIndex}:${accordionIndex}`;
  }

  public getAccordionExpanded(
    subSectionIndex: number,
    accordionIndex: number,
    defaultExpanded = false
  ): boolean {
    const key = this.getAccordionKey(subSectionIndex, accordionIndex);
    return this.accordionExpandedDict()[key] ?? defaultExpanded;
  }

  public setAccordionExpanded(subSectionIndex: number, accordionIndex: number, value: boolean): void {
    const key = this.getAccordionKey(subSectionIndex, accordionIndex);
    this.accordionExpandedDict.set({ ...this.accordionExpandedDict(), [key]: value });
  }

  public setAllAccordionsExpanded(subSectionIndex: number, expanded: boolean): void {
    const subSection = this.subSections()[subSectionIndex];
    if (!subSection || subSection.subSectionProps.subSectionType !== SubSectionType.accordionContainer) {
      return;
    }

    const next = { ...this.accordionExpandedDict() };
    subSection.subSectionProps.subSectionContent.accordions.forEach((_, accordionIndex) => {
      next[this.getAccordionKey(subSectionIndex, accordionIndex)] = expanded;
    });
    this.accordionExpandedDict.set(next);
  }

  public setExpandedAccordions(subSectionIndex: number, expandedIndexes: number[]): void {
    const subSection = this.subSections()[subSectionIndex];
    if (!subSection || subSection.subSectionProps.subSectionType !== SubSectionType.accordionContainer) {
      return;
    }

    const expandedIndexesSet = new Set(expandedIndexes);
    const next = { ...this.accordionExpandedDict() };
    subSection.subSectionProps.subSectionContent.accordions.forEach((_, accordionIndex) => {
      next[this.getAccordionKey(subSectionIndex, accordionIndex)] = expandedIndexesSet.has(accordionIndex);
    });
    this.accordionExpandedDict.set(next);
  }

}
